import { css, html, nothing } from "lit";
import { artImage, type ChromeArt } from "../chrome/chromeArt";
import { fallbackLabels, type LabelTable, type Labels } from "../chrome/chromeLabels";
import { ChromeElement, defineChromeElement, pressFeedback, pressHaptic } from "../chrome/ChromeElement";
import { fitTexts } from "../chrome/fitText";
import type { FeaturePurchaseCardModel, FeaturePurchaseConfirmModel, FeaturePurchaseBindings, FeaturePurchasePort } from "./FeaturePurchasePort";
import { resolveLandscapeHeightMultiplier } from "@slot/presentation";

export const FEATURE_PURCHASE_LABELS = { title: ["feature_purchase.title", "Feature Purchase"], bet: ["common.bet", "Bet"], back: ["common.back", "Back"] } as const satisfies LabelTable;
export type FeaturePurchaseLabels = Labels<typeof FEATURE_PURCHASE_LABELS>;

/** `FeaturePurchasePanel.prefab` geometry (design units, y down). */
const BACKDROP_ALPHA = 193 / 255;
/** Header stretched across the viewport, 203.2 tall, ×m in landscape; the close 86.05 + 55 from the right edge. */
const HEADER = { height: 203.2, top: -2.338, titleSize: 50, closeRight: 141.05, closeTop: 98.49, closeSize: 110, landscapeMax: 1.5 } as const;
/** TopBar 580×188.6, top-centred (top 217.7 in a 224.1 host) or, in landscape, right-centred (right 75.1, ×m). */
const TOP_BAR = { width: 580, height: 188.6, top: 329.75, landscapeRight: 75.1, betButton: { size: 110, x: 178.6 }, title: { size: 46.2, top: 41.76 }, amount: { size: 55, top: 99.2 } } as const;
/** ListRoot: 464.7 below the top, 56.83 above the bottom, full width; the landscape list inset left 79.8, right 956.8, top −251.9. */
const LIST_ROOT = { top: 464.7, bottom: 56.83, landscape: { left: 79.8, right: 956.8, top: -251.9 } } as const;
const GRID = {
  mobile: { columns: 2, cardWidth: 450, cardHeight: 630, gap: 24, paddingTop: 0 },
  mobile_landscape: { columns: 3, cardWidth: 450, cardHeight: 650, gap: 24, paddingTop: 0 },
  desktop: { columns: 4, cardWidth: 600, cardHeight: 650, gap: 50, paddingTop: 50 },
} as const;
/** `FeaturePurchaseCard.prefab`: the rows inside a card, from its top or bottom. */
const CARD = { inset: 20, title: { height: 69.72, top: 12.45, size: 40 }, icon: { width: 390, height: 255.4, top: 87.04 }, description: { height: 99.49, bottom: 185.9, size: 40 }, amount: { inset: 50, height: 54.7, bottom: 134.5, size: 34.2 }, button: { height: 94.58, bottom: 20, labelSize: 40 } } as const;
/** `ConfirmPanel/PopupCard` 800×765.6 (max 800 wide; 1200 in mobile landscape). */
const CONFIRM = { width: 800, height: 765.6, landscapeMaxWidth: 1200, title: { top: 70.9, size: 40, width: 682.2, height: 69.72 }, icon: { top: 252.5, width: 508.8, height: 272.6 }, description: { top: 490.7, size: 40, width: 682.2, height: 132.3 }, amount: { top: 601.6, size: 55.4 }, button: { width: 379.3, height: 91.78, x: 189.6, top: 699.7, labelSize: 45 } } as const;

export const FEATURE_PURCHASE_TAG = "slot-feature-purchase";

/**
 * `<slot-feature-purchase>`: backdrop (tap closes), the header with the title and close, the top
 * bar with the bet controls, the offer cards in a per-shape grid, and the confirm step over them.
 * The list and the top bar hide (and take no taps) while the confirm is open; its popup takes the
 * taps on it, the header's close the taps on that, and a tap beside them does nothing.
 */
export class FeaturePurchaseElement extends ChromeElement implements FeaturePurchasePort {
  public static override properties = {
    ...ChromeElement.properties,
    open: { type: Boolean, reflect: true },
    confirmOpen: { type: Boolean, reflect: true, attribute: "confirm" },
    labels: { attribute: false },
    art: { attribute: false },
    cards: { state: true },
    prices: { state: true },
    enabledIds: { state: true },
    betAmount: { state: true },
    betPlusEnabled: { state: true },
    betMinusEnabled: { state: true },
    confirm: { state: true },
    confirmActionEnabled: { state: true },
  };

  public declare open: boolean;
  public declare confirmOpen: boolean;
  public declare labels: FeaturePurchaseLabels;
  public declare art: ChromeArt;
  private declare cards: FeaturePurchaseCardModel[];
  private declare prices: Record<string, string>;
  private declare enabledIds: Record<string, boolean>;
  private declare betAmount: string;
  private declare betPlusEnabled: boolean;
  private declare betMinusEnabled: boolean;
  private declare confirm: FeaturePurchaseConfirmModel | null;
  private declare confirmActionEnabled: boolean;
  private bindings: FeaturePurchaseBindings | null = null;

  public constructor() {
    super();
    this.open = false;
    this.confirmOpen = false;
    this.labels = fallbackLabels(FEATURE_PURCHASE_LABELS);
    this.art = {};
    this.cards = [];
    this.prices = {};
    this.enabledIds = {};
    this.betAmount = "";
    this.betPlusEnabled = true;
    this.betMinusEnabled = true;
    this.confirm = null;
    this.confirmActionEnabled = true;
  }

  public static override styles = css`
    :host { display: none; position: absolute; inset: 0; pointer-events: auto; color: #000; font-weight: 500; opacity: 0; }
    :host([open]) { display: block; animation: fade-in 0.1s linear forwards; }
    @keyframes fade-in { to { opacity: 1; } }
    .backdrop { position: absolute; inset: 0; background: rgba(0, 0, 0, ${BACKDROP_ALPHA}); }
    .header { position: absolute; left: 0; top: ${HEADER.top}px; height: ${HEADER.height}px; transform-origin: top left; color: #fff; }
    .title { position: absolute; left: 0; right: 0; top: 50%; transform: translateY(-50%); text-align: center; font-size: ${HEADER.titleSize}px; line-height: 1; white-space: nowrap; }
    .icon-button { margin: 0; padding: 0; border: 0; background: transparent no-repeat center / 100% 100%; cursor: pointer; touch-action: manipulation; -webkit-tap-highlight-color: transparent; }
    .close { position: absolute; right: ${HEADER.closeRight}px; top: ${HEADER.closeTop}px; width: ${HEADER.closeSize}px; height: ${HEADER.closeSize}px; transform: translate(50%, -50%); }
    .close:hover { filter: brightness(0.39); }
    .close:active { filter: brightness(0.73); }
    .top-bar { position: absolute; width: ${TOP_BAR.width}px; height: ${TOP_BAR.height}px; border-radius: 16px; background: #e0e0e0; color: #363636; text-align: center; }
    .top-bar .bet-title, .top-bar .bet-amount { pointer-events: none; }
    .top-bar .bet-title { position: absolute; left: 0; right: 0; top: ${TOP_BAR.title.top}px; transform: translateY(-50%); font-size: ${TOP_BAR.title.size}px; line-height: 1; }
    .top-bar .bet-amount { position: absolute; left: 0; right: 0; top: ${TOP_BAR.amount.top}px; transform: translateY(-50%); font-size: ${TOP_BAR.amount.size}px; line-height: 1; }
    .bet-button { position: absolute; top: 50%; width: ${TOP_BAR.betButton.size}px; height: ${TOP_BAR.betButton.size}px; transform: translate(-50%, -50%); }
    .bet-button.plus { left: calc(50% + ${TOP_BAR.betButton.x}px); }
    .bet-button.minus { left: calc(50% - ${TOP_BAR.betButton.x}px); }
    .bet-button:hover { filter: brightness(0.8); }
    .bet-button:active { filter: brightness(0.45); }
    .bet-button[disabled] { filter: brightness(0.28); cursor: default; }
    .hidden { opacity: 0; pointer-events: none; }
    [data-fit] { display: inline-block; max-width: none; transform-origin: center; }
    .nowrap { white-space: nowrap; }
    .list { position: absolute; overflow-x: hidden; overflow-y: auto; overscroll-behavior: contain; touch-action: pan-y; }
    .grid { display: grid; justify-content: center; justify-items: center; box-sizing: border-box; }
    .card { position: relative; border-radius: 16px; background: #e0e0e0; text-align: center; }
    .card .row { position: absolute; left: ${CARD.inset}px; right: ${CARD.inset}px; display: flex; align-items: center; justify-content: center; overflow: hidden; }
    .card .title { position: absolute; top: ${CARD.title.top}px; height: ${CARD.title.height}px; font-size: ${CARD.title.size}px; transform: none; }
    .card .icon { position: absolute; left: 50%; top: ${CARD.icon.top}px; width: ${CARD.icon.width}px; height: ${CARD.icon.height}px; transform: translateX(-50%); background: transparent no-repeat center / contain; }
    .card .description { bottom: ${CARD.description.bottom}px; height: ${CARD.description.height}px; font-size: ${CARD.description.size}px; line-height: 40px; }
    .card .amount { left: ${CARD.amount.inset}px; right: ${CARD.amount.inset}px; bottom: ${CARD.amount.bottom}px; height: ${CARD.amount.height}px; font-size: ${CARD.amount.size}px; color: #1d970c; }
    .action { position: absolute; left: ${CARD.inset}px; right: ${CARD.inset}px; bottom: ${CARD.button.bottom}px; height: ${CARD.button.height}px; border: 0; border-radius: 16px; padding: 0; color: #fff; font: inherit; font-size: ${CARD.button.labelSize}px; font-weight: 500; cursor: pointer; touch-action: manipulation; -webkit-tap-highlight-color: transparent; }
    .action.purchase { background: #00aa5a; }
    .action.purchase:hover { background: #008c4b; }
    .action.purchase:active { background: #006e3c; }
    .action.activate { background: #ff8000; }
    .action.activate:hover { background: #dc6e00; }
    .action.activate:active { background: #b95c00; }
    .action[disabled], .action[disabled]:hover { background: #7c7c7c; cursor: default; }
    /* The confirm's layer spans the panel to centre its popup, which alone takes taps: the header's close stays live over it (until 2026-10-16 the layer took every tap, so the close showed and did nothing). */
    .confirm { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; pointer-events: none; }
    .popup { position: relative; width: ${CONFIRM.width}px; height: ${CONFIRM.height}px; border-radius: 16px; background: #e0e0e0; text-align: center; transform: scale(var(--confirm-scale, 1)); pointer-events: auto; }
    .popup .row { position: absolute; left: 50%; transform: translate(-50%, -50%); display: flex; align-items: center; justify-content: center; overflow: hidden; }
    .popup .title { top: ${CONFIRM.title.top}px; width: ${CONFIRM.title.width}px; height: ${CONFIRM.title.height}px; font-size: ${CONFIRM.title.size}px; }
    .popup .icon { position: absolute; left: 50%; top: ${CONFIRM.icon.top}px; width: ${CONFIRM.icon.width}px; height: ${CONFIRM.icon.height}px; transform: translate(-50%, -50%); background: transparent no-repeat center / contain; }
    .popup .description { top: ${CONFIRM.description.top}px; width: ${CONFIRM.description.width}px; height: ${CONFIRM.description.height}px; font-size: ${CONFIRM.description.size}px; line-height: 40px; }
    .popup .amount { top: ${CONFIRM.amount.top}px; width: 100%; font-size: ${CONFIRM.amount.size}px; color: #1d970c; }
    .popup .action { position: absolute; top: ${CONFIRM.button.top}px; width: ${CONFIRM.button.width}px; height: ${CONFIRM.button.height}px; left: auto; right: auto; bottom: auto; transform: translate(-50%, -50%); font-size: ${CONFIRM.button.labelSize}px; }
    .popup .back { left: calc(50% - ${CONFIRM.button.x}px); background: #ffc200; }
    .popup .back:hover { background: #ffd24d; }
    .popup .back:active { background: #d9a500; }
    .popup .primary { left: calc(50% + ${CONFIRM.button.x}px); }
  `;

  public bind(bindings: FeaturePurchaseBindings): void {
    this.bindings = bindings;
  }

  public setOpen(open: boolean): void {
    this.open = open;
    if (!open) this.confirmOpen = false;
    if (open) this.resetScroll();
  }

  public setCards(cards: readonly FeaturePurchaseCardModel[]): void {
    this.cards = [...cards];
  }

  public setCardPrice(offerId: string, text: string): void {
    this.prices = { ...this.prices, [offerId]: text };
  }

  public setCardActionEnabled(offerId: string, enabled: boolean): void {
    this.enabledIds = { ...this.enabledIds, [offerId]: enabled };
  }

  public setCardIcon(offerId: string, iconUrl: string | null): void {
    this.cards = this.cards.map((card) => (card.id === offerId ? { ...card, iconUrl } : card));
  }

  public setBetAmount(text: string): void {
    this.betAmount = text;
  }

  public setBetPlusEnabled(enabled: boolean): void {
    this.betPlusEnabled = enabled;
  }

  public setBetMinusEnabled(enabled: boolean): void {
    this.betMinusEnabled = enabled;
  }

  public setConfirm(model: FeaturePurchaseConfirmModel): void {
    this.confirm = model;
  }

  public setConfirmOpen(open: boolean): void {
    this.confirmOpen = open;
  }

  public setConfirmActionEnabled(enabled: boolean): void {
    this.confirmActionEnabled = enabled;
  }

  protected override updated(): void {
    fitTexts(this.renderRoot);
  }

  private resetScroll(): void {
    const list = this.renderRoot.querySelector<HTMLElement>(".list");
    if (list) list.scrollTop = 0;
  }

  /** The prefab's widgets + the landscape compensators over the design viewport. */
  private layout() {
    const v = this.view.design;
    const m = resolveLandscapeHeightMultiplier({ isMobileLandscape: this.isLandscape, frameWidth: this.view.css.width, frameHeight: this.view.css.height, maxMultiplier: HEADER.landscapeMax });
    const grid = GRID[this.view.shape];
    const list = this.isLandscape
      ? { left: LIST_ROOT.landscape.left, top: LIST_ROOT.top + LIST_ROOT.landscape.top, width: Math.max(1, v.width - LIST_ROOT.landscape.left - LIST_ROOT.landscape.right), height: Math.max(1, v.height - LIST_ROOT.top - LIST_ROOT.landscape.top - LIST_ROOT.bottom) }
      : { left: 0, top: LIST_ROOT.top, width: v.width, height: Math.max(1, v.height - LIST_ROOT.top - LIST_ROOT.bottom) };
    // The most columns that fit the list width (cards scaled ×m in landscape), capped per shape.
    const cardScale = this.isLandscape ? m : 1;
    const cardWidth = grid.cardWidth * cardScale;
    const columns = Math.max(1, Math.min(grid.columns, this.cards.length, Math.floor((list.width + grid.gap) / (cardWidth + grid.gap))));
    const confirmScale = this.isLandscape ? Math.min(CONFIRM.landscapeMaxWidth / CONFIRM.width, v.height / CONFIRM.height) : Math.min(1, v.width / CONFIRM.width);
    return { v, m, grid, list, cardScale, columns, confirmScale };
  }

  private cardTemplate(card: FeaturePurchaseCardModel, grid: { cardWidth: number; cardHeight: number }, scale: number) {
    const enabled = this.enabledIds[card.id] ?? false;
    return html`<div class="card" data-part="card" data-offer=${card.id} style="width: ${grid.cardWidth}px; height: ${grid.cardHeight}px; transform: scale(${scale}); transform-origin: top center">
      <div class="row title" data-part="card-title"><span data-fit class="nowrap">${card.title}</span></div>
      <div class="icon" style=${card.iconUrl ? `background-image: url("${card.iconUrl}")` : "display: none"}></div>
      <div class="row description" data-part="card-description"><span data-fit>${card.description}</span></div>
      <div class="row amount" data-part="card-price"><span data-fit class="nowrap">${this.prices[card.id] ?? ""}</span></div>
      <button class="action ${card.style}" data-part="card-action" type="button" ?disabled=${!enabled} @pointerdown=${pressFeedback} @click=${() => this.bindings?.onCardAction(card.id)}><span data-fit class="nowrap">${card.actionLabel}</span></button>
    </div>`;
  }

  /** The backdrop closes the panel from the list; beside the confirm a tap does nothing, as it did under the confirm's layer: its Back and the close leave it. */
  private readonly onBackdropTap = (): void => {
    if (!this.confirmOpen) this.bindings?.onBackdrop();
  };

  protected override render() {
    const { v, m, grid, list, cardScale, columns, confirmScale } = this.layout();
    const l = this.labels;
    const listHidden = this.confirmOpen ? "hidden" : "";
    const topBarStyle = this.isLandscape
      ? `right: ${TOP_BAR.landscapeRight}px; top: 50%; transform: translateY(-50%) scale(${m}); transform-origin: center right`
      : `left: 50%; top: ${TOP_BAR.top}px; transform: translate(-50%, -50%)`;
    const c = this.confirm;
    return html`
      <div class="backdrop" @click=${this.onBackdropTap}></div>
      <div class="header" style="width: ${v.width / m}px; transform: scale(${m})">
        <div class="title" data-part="title">${l.title}</div>
        <button class="icon-button close" data-part="close" type="button" style=${artImage(this.art, "closeRed")} @pointerdown=${pressFeedback} @click=${() => this.bindings?.onClose()}></button>
      </div>
      <div class="top-bar ${listHidden}" data-part="top-bar" style=${topBarStyle}>
        <button class="icon-button bet-button minus" data-part="bet-minus" type="button" ?disabled=${!this.betMinusEnabled} style=${artImage(this.art, "minusCircle")} @pointerdown=${pressHaptic} @click=${() => this.bindings?.onBetMinus()}></button>
        <div class="bet-title">${l.bet}</div>
        <div class="bet-amount" data-part="bet-amount"><span data-fit class="nowrap">${this.betAmount}</span></div>
        <button class="icon-button bet-button plus" data-part="bet-plus" type="button" ?disabled=${!this.betPlusEnabled} style=${artImage(this.art, "plusCircle")} @pointerdown=${pressHaptic} @click=${() => this.bindings?.onBetPlus()}></button>
      </div>
      <div class="list ${listHidden}" style="left: ${list.left}px; top: ${list.top}px; width: ${list.width}px; height: ${list.height}px">
        <div class="grid" style="grid-template-columns: repeat(${columns}, ${grid.cardWidth * cardScale}px); grid-auto-rows: ${grid.cardHeight * cardScale}px; gap: ${grid.gap}px; padding-top: ${grid.paddingTop}px">
          ${this.cards.map((card) => this.cardTemplate(card, grid, cardScale))}
        </div>
      </div>
      ${this.confirmOpen && c
        ? html`<div class="confirm" data-part="confirm">
            <div class="popup" style="--confirm-scale: ${confirmScale}">
              <div class="row title" data-part="confirm-title"><span data-fit class="nowrap">${c.title}</span></div>
              <div class="icon" style=${c.iconUrl ? `background-image: url("${c.iconUrl}")` : "display: none"}></div>
              <div class="row description" data-part="confirm-description"><span data-fit>${c.description}</span></div>
              <div class="row amount" data-part="confirm-amount"><span data-fit class="nowrap">${c.amountText}</span></div>
              <button class="action back" data-part="confirm-back" type="button" @pointerdown=${pressFeedback} @click=${() => this.bindings?.onConfirmBack()}><span data-fit class="nowrap">${l.back}</span></button>
              <button class="action primary ${c.style}" data-part="confirm-action" type="button" ?disabled=${!this.confirmActionEnabled} @pointerdown=${pressFeedback} @click=${() => this.bindings?.onConfirmPurchase()}><span data-fit class="nowrap">${c.actionLabel}</span></button>
            </div>
          </div>`
        : nothing}
    `;
  }
}

export function defineFeaturePurchaseElement(): void {
  defineChromeElement(FEATURE_PURCHASE_TAG, FeaturePurchaseElement);
}
